/**
 * The canonical, synchronous reader of the TrackedX click id.
 *
 * It does NOT go through the SDK. `src/templates/main.tsx` loads `/affiliates-sdk.min.js` with
 * `strategy='afterInteractive'` when the entry URL carries a non-empty `clickId` and `'lazyOnload'`
 * otherwise (`sdkStrategy`), and only calls `Affiliate.init()` inside `onReady`. Neither strategy runs
 * the script before hydration, so on a direct landing from a partner link (`/pricing?clickId=…`,
 * `/trial?clickId=…`) every mount effect runs BEFORE the SDK exists: `window.Affiliate` is undefined,
 * the cookie has not been written yet and the whole campaign silently becomes a no-op. Reading the source of truth here — cookie first, then the
 * URL, the same precedence the SDK's `setCookie()`/`getCookie()` pair has — makes the value available
 * from the very first render, with or without the SDK.
 *
 * Read-only as far as the COOKIE goes, on purpose: writing `np_affiliate` (domain, expiry, consent
 * dialog) stays with the SDK, and nothing here should ever write it. Our own session-scoped copy is a
 * different thing entirely and lives in `src/utils/click-id-store.ts`, written from exactly one place
 * (`useClickIdCapture`, in an effect) — never from this module, which is called during render by nine
 * gateway components.
 */

import { readStoredClickId } from './click-id-store';
import { sanitizeClickId } from './sanitize-click-id';

// Written by `Affiliate.setCookie()` as `np_affiliate=<base64 of {"clickId":"…"}>`. The SDK matches it
// with `startsWith('np_affiliate')`, which also swallows `np_affiliate_x`; we match the name exactly.
const CLICK_ID_COOKIE = 'np_affiliate';

/**
 * The click id inside the `np_affiliate` cookie, or `undefined` — never a throw.
 *
 * The SDK's own `getCookie()` does `JSON.parse(atob(t))` with no try around it, so a cookie some other
 * script (or an old release) left malformed took down whatever called it: `fetchCatalog` turned into a
 * rejected action and `/pricing` rendered an empty catalog with no error state, and `clickIdBody()`
 * threw synchronously out of the registration call. This is the one place that ends it.
 */
function readClickIdFromCookie(): string | undefined {
  try {
    const entry = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.slice(0, cookie.indexOf('=')) === CLICK_ID_COOKIE);

    if (!entry) return undefined;

    // From the FIRST `=`, not `split('=')[1]` like the SDK: base64 padding is made of `=` and the
    // SDK's split throws it away, which corrupts every value whose length is not a multiple of 4.
    const encoded = decodeURIComponent(entry.slice(entry.indexOf('=') + 1));

    if (!encoded) return undefined;

    const padded = encoded.padEnd(encoded.length + ((4 - (encoded.length % 4)) % 4), '=');
    const payload: unknown = JSON.parse(atob(padded));

    if (!payload || typeof payload !== 'object') return undefined;

    return sanitizeClickId((payload as { clickId?: unknown }).clickId);
  } catch {
    return undefined;
  }
}

/** The `?clickId=` the partner link carries, before the SDK has had the chance to turn it into a cookie. */
function readClickIdFromUrl(): string | undefined {
  try {
    return sanitizeClickId(new URL(window.location.href).searchParams.get('clickId'));
  } catch {
    return undefined;
  }
}

/**
 * The TrackedX click id, in the shape the API reads it: `click_id`, snake_case.
 *
 * Cookie wins over the URL, because that is the precedence the SDK has (`setCookie()` only writes when
 * there is no cookie, `getCookie()` answers the cookie). Inverting it would let a hand-typed query
 * param resolve one campaign on the storefront while the checkout, reading the cookie, charges another.
 *
 * Our own stored copy comes last, and only as a fallback (`src/utils/click-id-store.ts`). A fresh
 * partner link in the URL has to beat a value left over from earlier in the same tab, and the cookie —
 * which is what the SDK and TrackedX attribution actually use — has to beat both. The store exists for
 * the case where the cookie was never written at all, where the alternative is not "a different
 * campaign" but "no campaign".
 */
export function getClickId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  return readClickIdFromCookie() ?? readClickIdFromUrl() ?? readStoredClickId();
}

/**
 * The `click_id` fragment to merge into a request body, empty when there is no click id.
 *
 * A blank value has to become an omitted key, never an empty string.
 *
 * Spread it BEFORE the caller's own fields so an explicit `click_id` still wins.
 */
export function clickIdBody(): { click_id?: string } {
  const clickId = getClickId();

  return clickId ? { click_id: clickId } : {};
}
